import React, { useContext } from "react";
import { PatientContext } from "../context/PatientContext";
import { AppointmentContext } from "../context/AppointmentContext";
import { FiUser } from "react-icons/fi";
import Button from "../components/Button";
import SearchInput from "../components/SearchInput";
import { useVideoCall } from "../context/VideoCallContext";
import { useNotification } from "../context/NotificationContext";
import { useNavigate } from "react-router-dom";

const fallbackPatients = [
  {
    id: 1,
    name: "Aman",
    gender: "Male",
    age: 22,
    avatar: null,
    lastAppointment: "11 Nov, 2024",
    nextAppointment: "17 Nov, 2024",
    time: "9:00 am",
  },
  {
    id: 2,
    name: "Mohan",
    gender: "Male",
    age: 25,
    avatar: null,
    lastAppointment: "15 Nov, 2024",
    nextAppointment: "19 Nov, 2024",
    time: "9:00 am",
  },
];
const fallbackAppointments = [
  {
    id: 1,
    name: "Aman",
    age: 22,
    date: "17/11/24",
    time: "10 a.m.",
    status: "online",
    joinable: true,
  },
  {
    id: 2,
    name: "Mohan",
    age: 22,
    date: "17/11/24",
    time: "10 a.m.",
    status: "offline",
    joinable: false,
  },
  {
    id: 3,
    name: "Rohan",
    age: 22,
    date: "17/11/24",
    time: "10 a.m.",
    status: "offline",
    joinable: false,
  },
];

const Dashboard = () => {
  const { patients, setPatients } = useContext(PatientContext);
  const { appointments, setAppointments } = useContext(AppointmentContext);
  const { startCall } = useVideoCall();
  const { notify } = useNotification();
  const navigate = useNavigate();

  // Search state
  const [patientSearch, setPatientSearch] = React.useState("");
  const [appointmentSearch, setAppointmentSearch] = React.useState("");

  // Populate demo data if empty
  React.useEffect(() => {
    if (!patients || patients.length === 0) setPatients(fallbackPatients);
    if (!appointments || appointments.length === 0)
      setAppointments(fallbackAppointments);
    // eslint-disable-next-line
  }, []);

  // Filtered data
  const filteredPatients = patients.filter(
    (p) =>
      p.name.toLowerCase().includes(patientSearch.toLowerCase()) ||
      p.gender?.toLowerCase().includes(patientSearch.toLowerCase()) ||
      p.lastAppointment?.toLowerCase().includes(patientSearch.toLowerCase()) ||
      p.nextAppointment?.toLowerCase().includes(patientSearch.toLowerCase())
  );
  const filteredAppointments = appointments.filter(
    (a) =>
      a.name.toLowerCase().includes(appointmentSearch.toLowerCase()) ||
      a.date?.toLowerCase().includes(appointmentSearch.toLowerCase()) ||
      a.time?.toLowerCase().includes(appointmentSearch.toLowerCase())
  );

  const handleJoinCall = (appointment) => {
    if (!appointment.status === "online") {
      notify.warning("Patient is not online yet");
      return;
    }
    navigate("/video-call");
  };

  return (
    <div className="padding-x pt-16 pb-10 min-h-screen bg-gray-50 w-full">
      {/* Today's Appointment Card */}
      <div className="bg-white rounded-xl shadow-md p-6 mb-10 max-w-4xl mx-auto w-full">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
          <h3 className="font-semibold text-base sm:text-lg lg:text-xl text-gray-800">
            Today's Appointment
          </h3>
          <div className="w-full sm:w-64">
            <SearchInput
              value={appointmentSearch}
              onChange={(e) => setAppointmentSearch(e.target.value)}
              placeholder="Search appointments..."
            />
          </div>
        </div>
        {/* Responsive appointments list: cards for xs, table for sm+ */}
        <div className="space-y-4 sm:space-y-0">
          {/* Cards for xs screens */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 md:hidden">
            {filteredAppointments.length === 0 ? (
              <div className="col-span-full text-center text-gray-400 py-8">
                No appointments found.
              </div>
            ) : (
              <>
                {filteredAppointments.map((a, idx) => (
                  <div
                    key={a.id || idx}
                    className="bg-white rounded-lg shadow p-4 flex flex-col gap-2 transition-transform duration-200 hover:scale-[1.02] hover:shadow-lg cursor-pointer"
                  >
                    <div className="flex items-center gap-2">
                      <span
                        className={`inline-block w-2 h-2 rounded-full mr-1 align-middle ${
                          a.status === "online"
                            ? "bg-green-500"
                            : "bg-orange-400"
                        }`}
                      ></span>
                      <span className="font-medium text-gray-900">
                        {a.name}
                      </span>
                    </div>
                    <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-gray-600">
                      <span>Age: {a.age}</span>
                      <span>Date: {a.date}</span>
                      <span>Time: {a.time}</span>
                    </div>
                    {a.joinable && (
                      <div className="mt-2">
                        <Button
                          text="Join Now"
                          className="!text-xs !py-1.5 !px-4 !rounded-md !shadow-sm font-semibold w-full"
                          onClick={() => handleJoinCall(a)}
                        />
                      </div>
                    )}
                  </div>
                ))}
              </>
            )}
          </div>
          {/* Table for sm and up */}
          <div className="overflow-x-auto hidden sm:block">
            <table className="w-full text-left text-xs sm:text-sm">
              <thead>
                <tr className="text-gray-500">
                  <th className="py-2 sm:py-3 px-2 sm:px-4 font-normal text-base">
                    Patient Name
                  </th>
                  <th className="py-2 sm:py-3 px-2 sm:px-4 font-normal text-base">
                    Patient Age
                  </th>
                  <th className="py-2 sm:py-3 px-2 sm:px-4 font-normal hidden md:table-cell text-base">
                    Date
                  </th>
                  <th className="py-2 sm:py-3 px-2 sm:px-4 font-normal text-base">
                    Time
                  </th>
                  <th className="py-2 sm:py-3 px-2 sm:px-4 font-normal text-base"></th>
                </tr>
              </thead>
              <tbody>
                {filteredAppointments.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="text-center text-gray-400 py-8">
                      No appointments found.
                    </td>
                  </tr>
                ) : (
                  filteredAppointments.map((a, idx) => (
                    <tr
                      key={a.id || idx}
                      className="bg-white hover:bg-orange-50 transition-shadow duration-200 hover:shadow-md"
                    >
                      <td className="py-2 sm:py-3 px-2 sm:px-4">
                        <div className="flex items-center gap-1.5">
                          <span
                            className={`inline-block w-2 h-2 rounded-full mr-1 align-middle ${
                              a.status === "online"
                                ? "bg-green-500"
                                : "bg-orange-400"
                            }`}
                          ></span>
                          {a.name}
                        </div>
                      </td>
                      <td className="py-2 sm:py-3 px-2 sm:px-4">{a.age}</td>
                      <td className="py-2 sm:py-3 px-2 sm:px-4 hidden md:table-cell">
                        {a.date}
                      </td>
                      <td className="py-2 sm:py-3 px-2 sm:px-4">{a.time}</td>
                      <td className="py-2 sm:py-3 px-2 sm:px-4">
                        {a.joinable && (
                          <Button
                            text="Join Now"
                            className="!text-xs !py-1.5 !px-4 !rounded-md !shadow-sm font-semibold"
                            onClick={() => handleJoinCall(a)}
                          />
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Patient's List Card - now below */}
      <div className="bg-white rounded-xl shadow-md p-6 max-w-6xl mx-auto w-full">
        <h3 className="font-semibold mb-6 text-base sm:text-lg lg:text-xl text-gray-800">
          Patient's List
        </h3>
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
          <div className="w-full sm:w-64">
            <SearchInput
              value={patientSearch}
              onChange={(e) => setPatientSearch(e.target.value)}
              placeholder="Search patients..."
            />
          </div>
        </div>
        {/* Responsive patients list: cards for xs and sm, table for md+ */}
        <div className="space-y-4 md:space-y-0">
          {/* Cards for xs and sm screens */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 md:hidden">
            {filteredPatients.length === 0 ? (
              <div className="col-span-full text-center text-gray-400 py-8">
                No patients found.
              </div>
            ) : (
              <>
                {filteredPatients.map((p, idx) => (
                  <div
                    key={p.id || idx}
                    className="bg-white rounded-lg shadow p-4 flex flex-col gap-2 text-xs sm:text-sm transition-transform duration-200 hover:scale-[1.02] hover:shadow-lg cursor-pointer"
                  >
                    <div className="flex items-center gap-2 mb-2">
                      {p.avatar ? (
                        <img
                          src={p.avatar}
                          alt={p.name}
                          className="h-7 w-7 rounded-full object-cover"
                        />
                      ) : (
                        <span className="h-7 w-7 rounded-full flex items-center justify-center bg-gray-200 text-gray-500">
                          <FiUser className="h-4 w-4" />
                        </span>
                      )}
                      <span className="font-medium text-gray-900">
                        {p.name}
                      </span>
                    </div>
                    <dl className="grid grid-cols-2 gap-x-2 gap-y-1">
                      <dt className="text-gray-500">Gender:</dt>
                      <dd className="text-gray-900 truncate max-w-[110px]">
                        {p.gender}
                      </dd>
                      <dt className="text-gray-500">Age:</dt>
                      <dd className="text-gray-900 truncate max-w-[110px]">
                        {p.age}
                      </dd>
                      <dt className="text-gray-500">Last Appointment:</dt>
                      <dd className="text-gray-900 truncate max-w-[110px]">
                        {p.lastAppointment}
                      </dd>
                      <dt className="text-gray-500">Next Appointment:</dt>
                      <dd className="text-gray-900 truncate max-w-[110px]">
                        {p.nextAppointment}
                      </dd>
                      <dt className="text-gray-500">Time:</dt>
                      <dd className="text-gray-900 truncate max-w-[110px]">
                        {p.time}
                      </dd>
                    </dl>
                  </div>
                ))}
              </>
            )}
          </div>
        </div>
        {/* Table for md and up */}
        <div className="overflow-x-auto hidden md:block">
          <table className="w-full text-left text-xs sm:text-sm">
            <thead>
              <tr className="text-gray-500">
                <th className="py-2 sm:py-3 px-2 sm:px-4 font-normal text-base">
                  Name
                </th>
                <th className="py-2 sm:py-3 px-2 sm:px-4 font-normal text-base">
                  Gender
                </th>
                <th className="py-2 sm:py-3 px-2 sm:px-4 font-normal text-base">
                  Age
                </th>
                <th className="py-2 sm:py-3 px-2 sm:px-4 font-normal text-base">
                  Last Appointment
                </th>
                <th className="py-2 sm:py-3 px-2 sm:px-4 font-normal text-base">
                  Next Appointment
                </th>
                <th className="py-2 sm:py-3 px-2 sm:px-4 font-normal text-base">
                  Time
                </th>
              </tr>
            </thead>
            <tbody>
              {filteredPatients.length === 0 ? (
                <tr>
                  <td colSpan={6} className="text-center text-gray-400 py-8">
                    No patients found.
                  </td>
                </tr>
              ) : (
                filteredPatients.map((p, idx) => (
                  <tr
                    key={p.id || idx}
                    className="bg-white hover:bg-orange-50 transition-shadow duration-200 hover:shadow-md"
                  >
                    <td className="py-2 sm:py-3 px-2 sm:px-4">
                      <div className="flex items-center gap-2">
                        {p.avatar ? (
                          <img
                            src={p.avatar}
                            alt={p.name}
                            className="h-7 w-7 rounded-full object-cover"
                          />
                        ) : (
                          <span className="h-7 w-7 rounded-full flex items-center justify-center bg-gray-200 text-gray-500">
                            <FiUser className="h-4 w-4" />
                          </span>
                        )}
                        <span>{p.name}</span>
                      </div>
                    </td>
                    <td className="py-2 sm:py-3 px-2 sm:px-4">{p.gender}</td>
                    <td className="py-2 sm:py-3 px-2 sm:px-4">{p.age}</td>
                    <td className="py-2 sm:py-3 px-2 sm:px-4">
                      {p.lastAppointment}
                    </td>
                    <td className="py-2 sm:py-3 px-2 sm:px-4">
                      {p.nextAppointment}
                    </td>
                    <td className="py-2 sm:py-3 px-2 sm:px-4">{p.time}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
